'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React from 'react';

import { getFormContact } from '@/app/[locale]/(default-layout)/(forms)/_components/form-contact-helper';
import {
    resolveFormPageText,
    resolveFormPageTitle,
    resolveFormImageUrl
} from '@/app/[locale]/(default-layout)/(forms)/_components/form-image-helper';
import FormContactLinks from '@/app/[locale]/(default-layout)/(forms)/_components/FormContactLinks';
import SchoolScreeningForm from '@/app/[locale]/(default-layout)/(forms)/_components/SchoolScreeningForm';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import CinemaDescriptionMarkdown from '@cineamo/ui/src/markdown/CinemaDescriptionMarkdown';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

const SchoolScreeningImage = loadAsset('/images/header/im-schoolScreening.jpg');

type SchoolScreeningPageClientProps = {
    domainConfiguration: DomainConfigurationDto;
    /** Fallback hero image (first cinema room picture), used when no image is configured. */
    hallImageUrl?: string;
};

const BulletPoint: React.FC<{ domainConfiguration: DomainConfigurationDto }> = ({ domainConfiguration }) => {
    return (
        <div
            className="size-[16px] rounded-full flex items-center justify-center flex-shrink-0 outline-none"
            style={{ backgroundColor: domainConfiguration.style.primaryColor }}>
            <div
                className="size-[6px] rounded-full"
                style={{
                    backgroundColor: domainConfiguration.style.onPrimaryColor
                }}
            />
        </div>
    );
};

const SchoolScreeningPageClient: React.FC<SchoolScreeningPageClientProps> = ({ domainConfiguration, hallImageUrl }) => {
    const t = useTranslations();
    const isMounted = useIsMounted();

    const { phone, phoneURI, email } = getFormContact(domainConfiguration, 'schoolScreening');

    // Custom image configured on the cinema app's "our services" forms section takes
    // precedence, then the hall picture.
    const heroImage = resolveFormImageUrl(domainConfiguration, 'schoolScreening', hallImageUrl) || SchoolScreeningImage;

    const customDescription = resolveFormPageText(domainConfiguration, 'schoolScreening');
    const customPageTitle = resolveFormPageTitle(domainConfiguration, 'schoolScreening');

    const bulletPointData = ['flexible-dates', 'movie-choice', 'school-rates', 'materials', 'snacks'].map((key) => ({
        title: t(`school-screening.info.${key}.title`),
        text: t(`school-screening.info.${key}.text`)
    }));

    return (
        <main className="min-h-screen">
            {/* Main Content */}
            <section className="py-24">
                <CenterLayout>
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Left Column - Content */}
                        <div className="space-y-12">
                            <div className="space-y-6">
                                {/* The page's only h1. Two of the sibling form pages open on an h2
                                    instead, which leaves an indexed page without a top-level heading —
                                    not a pattern worth copying onto a new one. */}
                                <h1
                                    className="text-headline-32"
                                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                    {customPageTitle || t('school-screening.hero.title')}
                                </h1>
                                <div
                                    className="text-body-16"
                                    style={{ color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8) }}>
                                    {customDescription ? (
                                        <CinemaDescriptionMarkdown
                                            description={customDescription}
                                            allowInlineHtml
                                        />
                                    ) : (
                                        t('school-screening.hero.description')
                                    )}
                                </div>
                            </div>

                            {/* Wayfinding, not editorial copy: the form is on the page whatever the intro
                                says, so this stays outside the customDescription guard. */}
                            <a
                                href="#school-screening-form"
                                className="inline-block px-32 py-6 rounded-4 text-body-bold-16 uppercase hover:opacity-80 transition-opacity"
                                style={{
                                    backgroundColor: domainConfiguration.style.primaryColor,
                                    color: domainConfiguration.style.onPrimaryColor
                                }}>
                                {t('school-screening.hero.form-cta')}
                            </a>

                            {/* The info list is default copy, so a custom page text replaces it too rather
                                than sitting above boilerplate the operator can neither edit nor remove. */}
                            {!customDescription && (
                                <div className="space-y-6">
                                    <h2
                                        className="text-headline-20 font-semibold"
                                        style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                        {t('school-screening.info.title')}
                                    </h2>
                                    <ul className="space-y-4">
                                        {bulletPointData.map((data, index) => (
                                            <li
                                                key={`BulletPoint#${index}`}
                                                className="flex items-start space-x-3">
                                                {/* The bullet centers on the first line rather than on the
                                                    whole block, which now wraps to two lines. */}
                                                <div className="flex-shrink-0 pt-4">
                                                    <BulletPoint domainConfiguration={domainConfiguration} />
                                                </div>
                                                <span
                                                    className="text-body-16"
                                                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                                    <strong>{data.title}</strong> {data.text}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Right Column - Image */}
                        <div className="relative aspect-[16/9] rounded-12 overflow-hidden">
                            {isMounted && (
                                <Image
                                    src={heroImage}
                                    alt={t('school-screening.page-title')}
                                    fill
                                    className="object-cover"
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                />
                            )}
                        </div>
                    </div>
                </CenterLayout>
            </section>

            {/* Request Form Section */}
            <section className="py-24">
                <CenterLayout>
                    <div className="max-w-2xl mx-auto">
                        <div className="text-center mb-16">
                            <h2
                                className="text-headline-32"
                                style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                {t('school-screening.form.title')}
                            </h2>
                        </div>

                        <SchoolScreeningForm domainConfiguration={domainConfiguration} />
                    </div>
                </CenterLayout>
            </section>

            {/* Contact Section */}
            <section
                className="mt-48 py-24 backdrop-blur-sm"
                style={{ backgroundColor: withOpacity(domainConfiguration.style.onBackgroundColor, 0.05) }}>
                <CenterLayout>
                    <div className="text-center space-y-8">
                        <h2
                            className="text-headline-32"
                            style={{ color: domainConfiguration.style.onBackgroundColor }}>
                            {t('school-screening.contact.question')}
                        </h2>
                        <p
                            className="text-body-16 max-w-2xl mx-auto"
                            style={{ color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8) }}>
                            {t('school-screening.contact.help-text')}
                        </p>
                        <FormContactLinks
                            namespace="school-screening"
                            phone={phone}
                            phoneURI={phoneURI}
                            email={email}
                        />
                    </div>
                </CenterLayout>
            </section>
        </main>
    );
};

export default SchoolScreeningPageClient;
